<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MasifiSettingsAPI from 'dashboard/api/masifi/masifiSettings';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useStore } from 'vuex';
import MasifiWaKnobsCard from './MasifiWaKnobsCard.vue';
import { MASIFI_WA_WINDOWS } from 'dashboard/helper/masifi/masifiWaMessageActions';

// Sección unificada de settings propias de Masifi ("Masifi Inbox"). Cada knob del backend
// (Masifi::Settings::REGISTRY) se renderiza como una card: módulos, comportamiento y WhatsApp QR.
const { t } = useI18n();
const store = useStore();

const isLoading = ref(true);
// Comportamiento per-cuenta (on/off simples del admin; REGISTRY scope :account): [key, sección i18n
// MASIFI_SETTINGS.<sección>.HEADER|DESCRIPTION]. Una card por knob, en este orden; agregar uno = una línea + sus textos.
const BEHAVIOR_KNOBS = [
  ['duplicate_lead_owner_alert', 'DUPLICATE_OWNER'],
  ['isolate_leads_on_reassign', 'ISOLATE_LEADS'],
  ['notify_on_new_conversation', 'NEW_CONVERSATION'],
  ['auto_accept_on_reply', 'AUTO_ACCEPT'],
  // Asignar al primer agente que responde (spec autoassign-sa-mfa-SPEC-PLAN.md Parte A §A; beta OFF).
  ['assign_on_first_reply', 'ASSIGN_ON_FIRST_REPLY'],
];
const behavior = ref({});
const crmEnabled = ref(true);
const whatsappQrEnabled = ref(true);
// Solo aparece si el owner habilitó feature_whatsapp_qr por cuenta (Masifi::Settings.resolved
// filtra por `requires`): sin entitlement, la card no se muestra (nada que apagar).
const hasWhatsappQr = ref(false);
// Marca y dominio propios (R1.2): solo aparece si el owner habilitó feature_custom_brand (el backend oculta
// custom_brand_enabled sin el entitlement, por `requires`). Apagarla corta el dominio al instante (Resolver); los
// webhooks y el widget siguen con la marca neutra (RA-4), por eso con bandejas así se confirma antes.
const hasCustomBrand = ref(false);
const customBrandEnabled = ref(true);
const customBrandPublicInboxes = ref(0);
const brandOffDialogRef = ref(null);
// Cards de WhatsApp QR: knobs per-cuenta que el backend solo expone con el entitlement QR (requires); cada card, además,
// solo con el módulo prendido por el admin. [key, etiqueta i18n, parámetros del texto].
const GROUP_KNOBS = [
  ['whatsapp_qr_group_leads', 'LEADS'],
  ['whatsapp_qr_group_auto_assign', 'AUTO_ASSIGN'],
  ['whatsapp_qr_group_auto_messages', 'AUTO_MESSAGES'],
  ['whatsapp_qr_group_bots', 'BOTS'],
];
// Editar / eliminar para todos (ON por defecto) + guardar el original de un eliminado ("Ver original", ON): el plazo del
// texto sale de la fuente única (config/masifi/…message_actions.yml).
const MESSAGE_ACTION_KNOBS = [
  [
    'whatsapp_qr_edit_messages',
    'EDIT',
    { minutes: MASIFI_WA_WINDOWS.edit / 60 },
  ],
  [
    'whatsapp_qr_delete_for_everyone',
    'DELETE_FOR_EVERYONE',
    { hours: MASIFI_WA_WINDOWS.revoke / 3600 },
  ],
  // Reaccionar (spec polish-1 §3.7): misma familia que editar / eliminar para todos.
  ['whatsapp_qr_react_messages', 'REACT'],
  ['whatsapp_qr_keep_deleted_original', 'KEEP_DELETED_ORIGINAL'],
];
// Conversaciones (spec polish-1 §3.4/§3.8): chequeo de número y avisos del contacto.
// Fase 2 (spec whatsapp-qr-phase2-history-pairing §4): importar el historial del teléfono al vincular (en prueba, OFF).
const CONVERSATION_KNOBS = [
  ['whatsapp_qr_number_check', 'NUMBER_CHECK'],
  ['whatsapp_qr_contact_events', 'CONTACT_EVENTS'],
  ['whatsapp_qr_history_import', 'HISTORY_IMPORT'],
];
const WA_KEYS = [
  ...GROUP_KNOBS,
  ...MESSAGE_ACTION_KNOBS,
  ...CONVERSATION_KNOBS,
].map(([key]) => key);
const waSettings = ref({});
const hasWaKnobs = knobs =>
  hasWhatsappQr.value &&
  whatsappQrEnabled.value &&
  knobs.every(([key]) => key in waSettings.value);
const hasGroupSettings = computed(() => hasWaKnobs(GROUP_KNOBS));
const hasMessageActionSettings = computed(() =>
  hasWaKnobs(MESSAGE_ACTION_KNOBS)
);
const hasConversationSettings = computed(() => hasWaKnobs(CONVERSATION_KNOBS));
// Única función de guardado optimista de la página (el valor ya se ve aplicado): guarda, avisa y, si la API falla, deshace
// con `revert`. refresh = recargar la cuenta del store (lo que el dashboard lee por enabled_features).
const saveSetting = async (key, value, revert, { refresh = false } = {}) => {
  try {
    await MasifiSettingsAPI.update({ [key]: value });
    useAlert(t('MASIFI_SETTINGS.API.SAVE_SUCCESS'));
    if (refresh) store.dispatch('accounts/get');
  } catch (error) {
    revert();
    useAlert(t('MASIFI_SETTINGS.API.SAVE_ERROR'));
  }
};
// Un knob de un mapa reactivo { key: valor } (comportamiento, WhatsApp QR).
const saveKnob = (target, key, value, options) => {
  const previous = target.value[key];
  target.value = { ...target.value, [key]: value };
  return saveSetting(
    key,
    value,
    () => {
      target.value = { ...target.value, [key]: previous };
    },
    options
  );
};
// Las acciones del menú del mensaje leen enabled_features (masifi_wa_*): refrescar la cuenta del store al guardar.
const updateWaSetting = (key, value) =>
  saveKnob(waSettings, key, value, { refresh: true });
// Los knobs de comportamiento los lee el backend: sin refrescar la cuenta.
const updateBehavior = (key, value) => saveKnob(behavior, key, value);
// Texto de la card de un knob de comportamiento.
const behaviorText = (section, part) =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -- secciones fijas de BEHAVIOR_KNOBS; una sección sin textos en es y en la caza MasifiSettingsIndex.spec.js ("textos de cada card en es y en")
  t(`MASIFI_SETTINGS.${section}.${part}`);

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await MasifiSettingsAPI.get();
    behavior.value = Object.fromEntries(
      BEHAVIOR_KNOBS.map(([key]) => [key, Boolean(data.settings?.[key])])
    );
    crmEnabled.value = Boolean(data.settings?.crm_enabled);
    hasWhatsappQr.value = 'whatsapp_qr_enabled' in (data.settings || {});
    whatsappQrEnabled.value = Boolean(data.settings?.whatsapp_qr_enabled);
    hasCustomBrand.value = 'custom_brand_enabled' in (data.settings || {});
    customBrandEnabled.value = Boolean(data.settings?.custom_brand_enabled);
    customBrandPublicInboxes.value = Number(
      data.impact?.custom_brand_public_inboxes || 0
    );
    waSettings.value = Object.fromEntries(
      WA_KEYS.filter(key => key in (data.settings || {})).map(key => [
        key,
        Boolean(data.settings[key]),
      ])
    );
  } catch (error) {
    useAlert(t('MASIFI_SETTINGS.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Un toggle de un ref suelto (módulos, marca): revertir = el valor contrario.
const persist = (key, value, model) =>
  saveSetting(key, value, () => {
    model.value = !value;
  });

const updateCrmEnabled = async value => {
  await persist('crm_enabled', value, crmEnabled);
  // Refresca el record de la cuenta en el store → el gate del sidebar (Policy feature-flag,
  // que lee account.features) re-evalúa crm_active? al instante, sin recargar la página.
  store.dispatch('accounts/get');
};
const updateWhatsappQrEnabled = async value => {
  await persist('whatsapp_qr_enabled', value, whatsappQrEnabled);
  // Mismo motivo que crmEnabled: feature_whatsapp_qr en enabled_features depende del gate
  // efectivo (whatsapp_qr_active?), y el panel/asistente de alta lo leen del store.
  store.dispatch('accounts/get');
};
// RA-4: apagar con bandejas públicas (widget/webhook) pide confirmación; el toggle no se mueve hasta confirmar.
const updateCustomBrandEnabled = value => {
  if (!value && customBrandPublicInboxes.value > 0) {
    customBrandEnabled.value = true;
    brandOffDialogRef.value?.open();
    return undefined;
  }
  return persist('custom_brand_enabled', value, customBrandEnabled);
};
const confirmCustomBrandOff = async () => {
  brandOffDialogRef.value?.close();
  customBrandEnabled.value = false;
  await persist('custom_brand_enabled', false, customBrandEnabled);
};

onMounted(fetchSettings);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('MASIFI_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('MASIFI_SETTINGS.TITLE')"
        :description="t('MASIFI_SETTINGS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-4 pb-8">
        <SettingsToggleSection
          v-model="crmEnabled"
          :header="t('MASIFI_SETTINGS.CRM_MODULE.HEADER')"
          :description="t('MASIFI_SETTINGS.CRM_MODULE.DESCRIPTION')"
          @update:model-value="updateCrmEnabled"
        />
        <SettingsToggleSection
          v-for="[key, section] in BEHAVIOR_KNOBS"
          :key="key"
          :model-value="Boolean(behavior[key])"
          :header="behaviorText(section, 'HEADER')"
          :description="behaviorText(section, 'DESCRIPTION')"
          @update:model-value="value => updateBehavior(key, value)"
        />
        <SettingsToggleSection
          v-if="hasWhatsappQr"
          v-model="whatsappQrEnabled"
          :header="t('MASIFI_SETTINGS.WHATSAPP_QR.HEADER')"
          :description="t('MASIFI_SETTINGS.WHATSAPP_QR.DESCRIPTION')"
          @update:model-value="updateWhatsappQrEnabled"
        />
        <SettingsToggleSection
          v-if="hasCustomBrand"
          v-model="customBrandEnabled"
          :header="t('MASIFI_SETTINGS.CUSTOM_BRAND.HEADER')"
          :description="t('MASIFI_SETTINGS.CUSTOM_BRAND.DESCRIPTION')"
          @update:model-value="updateCustomBrandEnabled"
        />
        <Dialog
          v-if="hasCustomBrand"
          ref="brandOffDialogRef"
          type="alert"
          :title="t('MASIFI_SETTINGS.CUSTOM_BRAND.CONFIRM_OFF.TITLE')"
          :description="
            t('MASIFI_SETTINGS.CUSTOM_BRAND.CONFIRM_OFF.DESCRIPTION', {
              count: customBrandPublicInboxes,
            })
          "
          :confirm-button-label="
            t('MASIFI_SETTINGS.CUSTOM_BRAND.CONFIRM_OFF.CONFIRM')
          "
          @confirm="confirmCustomBrandOff"
        />
        <MasifiWaKnobsCard
          v-if="hasGroupSettings"
          section="WHATSAPP_QR_GROUPS"
          :knobs="GROUP_KNOBS"
          :settings="waSettings"
          @update="updateWaSetting"
        />
        <MasifiWaKnobsCard
          v-if="hasMessageActionSettings"
          section="WHATSAPP_QR_MESSAGE_ACTIONS"
          :knobs="MESSAGE_ACTION_KNOBS"
          :settings="waSettings"
          @update="updateWaSetting"
        />
        <MasifiWaKnobsCard
          v-if="hasConversationSettings"
          section="WHATSAPP_QR_CONVERSATIONS"
          :knobs="CONVERSATION_KNOBS"
          :settings="waSettings"
          @update="updateWaSetting"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
